/**
 * Lazy Video Loading
 * Improves performance by loading videos only when they're about to enter the viewport
 * v1.0.0
 */

const lazyVideo = () => {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
        // Fallback: load all videos immediately for older browsers
        loadAllVideos();
        return;
    }

    const lazyVideoElements = document.querySelectorAll('[data-lazy-video]');

    if (lazyVideoElements.length === 0) return;

    // Create intersection observer with optimized settings
    const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const videoContainer = entry.target;
                loadVideo(videoContainer);
                observer.unobserve(videoContainer);
            }
        });
    }, {
        // Start loading when video is 50% visible or 200px from viewport
        rootMargin: '200px 0px',
        threshold: 0.1
    });

    // Observe all lazy video elements
    lazyVideoElements.forEach(videoEl => {
        videoObserver.observe(videoEl);
    });

    // Preload hero videos immediately (above the fold)
    const heroVideos = document.querySelectorAll('section [data-lazy-video]');
    heroVideos.forEach(videoEl => {
        // Load hero videos with a slight delay to prioritize critical resources
        setTimeout(() => {
            loadVideo(videoEl);
            videoObserver.unobserve(videoEl);
        }, 100);
    });
};

/**
 * Load a single video
 */
const loadVideo = (videoContainer) => {
    const videoSrc = videoContainer.getAttribute('data-video-src');

    if (!videoSrc || videoContainer.hasAttribute('data-video-loaded')) {
        return;
    }

    // Mark as loaded to prevent duplicate loading
    videoContainer.setAttribute('data-video-loaded', 'true');

    // Create a temporary container to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = videoSrc;

    // Get the iframe element
    const iframe = tempDiv.querySelector('iframe');

    if (iframe) {
        // Add loading optimization attributes
        iframe.setAttribute('loading', 'eager');
        iframe.setAttribute('importance', 'high');

        // Add fade-in animation
        iframe.style.opacity = '0';
        iframe.style.transition = 'opacity 0.5s ease-in-out';

        // Append to container
        videoContainer.appendChild(iframe);

        // Fade in once loaded
        iframe.addEventListener('load', () => {
            iframe.style.opacity = '1';

            // Hide poster image if it exists
            const posterImage = videoContainer.parentElement.querySelector('img');
            if (posterImage) {
                posterImage.style.transition = 'opacity 0.5s ease-in-out';
                posterImage.style.opacity = '0';

                // Remove poster after transition
                setTimeout(() => {
                    if (posterImage.parentElement) {
                        posterImage.remove();
                    }
                }, 500);
            }
        });

        // Handle loading errors
        iframe.addEventListener('error', () => {
            console.warn('Video failed to load:', iframe.src);
            // Keep poster image visible on error
        });
    }
};

/**
 * Fallback function for browsers without Intersection Observer
 */
const loadAllVideos = () => {
    const lazyVideoElements = document.querySelectorAll('[data-lazy-video]');
    lazyVideoElements.forEach(videoEl => {
        // Add a delay to prevent blocking critical resources
        setTimeout(() => loadVideo(videoEl), 500);
    });
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', lazyVideo);

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lazyVideo);
} else {
    lazyVideo();
}

export default lazyVideo;
